import { State } from '@meraki-internal/state';
import { WithLinks } from '../../caremap/WithLinks';
import { ICareMap } from '../../caremap/ICareMap';
import { CaremapNotesModelFactory } from './CaremapNotesModelFactory';
import { ICaremapNote, ICaremapNoteResponse, INoteReactions } from './NotesClient';
import { ReactionsModel } from '../../community/reactions/ReactionsModel';
import { IResourceChangedSocketMessage } from '../../web-sockets/SocketState';
import { ICareMapNoteTarget } from './ICareMapNoteTarget';

export class NotesModel extends State<Record<string, never>> {
    constructor(
        private careMap: WithLinks<ICareMap>,
        private factory: CaremapNotesModelFactory
    ) {
        super({});
        this.reactions = this.factory.reactionsFactory.create(this);
    };

    private notes: ICaremapNoteResponse[] = [];
    reactions: ReactionsModel;

    hasLoaded = false;

    onWebsocketMessage = async (message: IResourceChangedSocketMessage) => {
        // triggered when someone reacts to a note
        const reactedNote = this.notes.find(n => n.links.reactions.href.toLowerCase() === message.resourceToRefresh.toLowerCase());
        if (reactedNote){
            try {
                reactedNote.reactions = await this.factory.notesClient.getNoteReactions(reactedNote);
                this.setState({ });
            } catch (err: any) {
                this.factory.logger.error(`NotesModel.onWebsocketMessage failed updating reactions for noteId ${reactedNote.noteId}. Inner error: ${err.toString()}`);
            };
            return;
        }

        // triggered when recording state changes
        const updatedNote = this.notes.find(n => n.links.self.href === message.resourceToRefresh);
        if (updatedNote) {
            try {
                await this.refresh(message.resourceToRefresh);
            } catch (err: any) {
                this.factory.logger.error(`NotesModel.onWebsocketMessage failed updating recording for noteId ${updatedNote.noteId}. Inner error: ${err.toString()}`);
            };
            return;
        }
    };

    load = async () => {
        const notes = await this.factory.notesClient.getNotesForCareMap(this.careMap);
        this.notes = notes;
        this.hasLoaded = true;
        this.setState({});
    };

    refresh = async (resource: string) => {
        const updated = await this.factory.notesClient.getNote({ href: resource });
        const existing = this.notes.find(n => n.links.self.href === resource);
        if (existing) {
            Object.assign(existing, updated);
        } else {
            this.notes.push(updated);
        }
        this.setState({ });
    };

    canEdit = (note: ICaremapNote) => {
        return note.links.self && (note.links.self.actions || []).includes('put-note');
    };

    canDelete = (note: ICaremapNote) => {
        return note.links.self && (note.links.self.actions || []).includes('delete-note');
    };

    addNewNote = async (note: ICaremapNote) => {
        const response = await this.factory.notesClient.addNoteToCareMap({
            careMap: this.careMap,
            note: {
                ...note,

                // a signal so the API knows notifications can go out
                // that we're not an old client that POSTed on the first few key strokes
                isComplete: true
            }
        });

        // when running locally with bypass, note will already have been added via websocket
        // so only add it here if not already in the list
        if (!this.notes.some(n => n.noteId === response.noteId)) {
            this.notes.push(response);
            this.setState({});
        }

        return response;
    };

    putNote = async (note: WithLinks<ICaremapNote>) => {
        const response = await this.factory.notesClient.updateNote(note);
        this.notes = [
            ...this.notes.filter(n => n.noteId !== note.noteId),
            response
        ];
        this.setState({});
    };

    deleteNote = async (note: WithLinks<ICaremapNote>) => {
        const deletedNote = this.notes.find(n=>n.noteId === note.noteId);
        await this.factory.notesClient.deleteNote(note);

        if (deletedNote) {
            this.factory.tracking.onDeletedNote(deletedNote);
        }

        this.localDeleteNote(note);
    };

    localDeleteNote = (note: ICaremapNote) => {
        this.notes = this.notes.filter(n => n.noteId !== note.noteId);

        // we mutated state, let subscribers know
        this.setState({});
    };

    getNotes = (): ICaremapNoteResponse[] => {
        return this.notes;
    };

    getNotesForTarget = (noteTarget: ICareMapNoteTarget): ICaremapNote[] => {
        return this.notes.filter(n => this.isSameTarget(n.otherAttributes.noteTarget, noteTarget));
    };

    private isSameTarget = (l: ICareMapNoteTarget, r: ICareMapNoteTarget) => {
        return l.section === r.section && l.memberId === r.memberId;
    };

    getNote = (noteId: string): ICaremapNoteResponse | undefined => {
        return this.getNotes().find(n => n.noteId === noteId);
    };

    getAttachmentCount = () => {
        return this.notes.reduce((count, note) => {
            return count + note.attachments.length;
        }, 0);
    };

    updateLocalReactions = ({ note, reactions }: { note: ICaremapNote; reactions: INoteReactions; }) => {
        note.reactions = reactions;
        this.setState({});
    };

}
